// src/prototype/enterprise/decisions/DecisionsSurface.tsx
// ============================================================
// ENTERPRISE · DECISIONS SURFACE (Surface C · "1h") — the attributed decision record.
//
// Newest-first timeline from the spine (buildDecisionTimeline: getDecisionLog + scenario-saves),
// read-only. Filters by decision type, initiative, and run. Each governance decision carries a
// "View initiative evolution →" link (wired to the shared evolution view in Part B; inert here).
// Layout/styling match Enterprise Prototype.dc.html · Surface C.
// ============================================================

import React, { useMemo, useState } from 'react';
import {
  buildDecisionTimeline, decisionFilterOptions, DECISION_BADGE, DECISION_DOT,
  type DecisionKind,
} from './decisionsModel';
import { EvolutionView } from '../evolution/EvolutionView';

const TYPE_FILTERS: { label: string; value: 'All' | DecisionKind }[] = [
  { label: 'All', value: 'All' },
  { label: 'Approve', value: 'APPROVE' },
  { label: 'Defer', value: 'DEFER' },
  { label: 'Hold', value: 'HOLD' },
  { label: 'Kill', value: 'KILL' },
  { label: 'Saved', value: 'SAVED' },
];

export const DecisionsSurface: React.FC = () => {
  const entries = useMemo(() => buildDecisionTimeline(), []);
  const opts = useMemo(() => decisionFilterOptions(entries), [entries]);

  const [type, setType] = useState<'All' | DecisionKind>('All');
  const [initId, setInitId] = useState<'All' | number>('All');
  const [runId, setRunId] = useState<'All' | string>('All');
  const [evoId, setEvoId] = useState<number | null>(null);

  const filtered = useMemo(
    () =>
      entries.filter(
        (e) =>
          (type === 'All' || e.decision === type) &&
          (initId === 'All' || e.use_case_id === initId) &&
          (runId === 'All' || e.run_id === runId),
      ),
    [entries, type, initId, runId],
  );

  const selectClass = 'border border-[#e5e7eb] rounded-full px-3 py-1.5 text-[11px] font-medium text-[#374151] bg-white cursor-pointer';

  return (
    <div className="min-h-[calc(100vh-180px)] bg-[#F5F7FA]">
      <div className="px-6 py-6">
        <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
          {/* Header + filters */}
          <div className="flex flex-wrap justify-between items-center gap-3 px-5 py-3.5 border-b border-[#e5e7eb]">
            <div>
              <div className="text-base font-bold text-[#18365E]">Decision record</div>
              <div className="text-[11px] text-[#717182] mt-0.5">
                Every strategic decision — timestamped, attributed, tied to the run it was made under
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setType(f.value)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold border ${
                    type === f.value ? 'bg-[#18365E] text-white border-[#18365E]' : 'bg-white text-[#374151] border-[#e5e7eb] hover:border-[#18365E]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <select className={selectClass} value={initId} onChange={(e) => setInitId(e.target.value === 'All' ? 'All' : Number(e.target.value))}>
                <option value="All">All initiatives</option>
                {opts.initiatives.map((i) => (
                  <option key={i.id} value={i.id}>{i.title}</option>
                ))}
              </select>
              <select className={selectClass} value={runId} onChange={(e) => setRunId(e.target.value)}>
                <option value="All">All runs</option>
                {opts.runs.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Timeline */}
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-[11px] text-[#9ca3af]">No decisions match these filters.</div>
          ) : (
            filtered.map((d) => (
              <div key={d.id} className="flex gap-4 px-5 py-3.5 border-b border-[#f1f3f6]">
                <div className="w-[70px] flex-none text-right">
                  <div className="text-[11px] font-bold text-[#18365E]">{d.date}</div>
                  <div className="text-[9.5px] text-[#9ca3af]">{d.year}</div>
                </div>
                <div className="w-0.5 flex-none bg-[#e5e7eb] relative">
                  <div className="absolute top-1 -left-1 w-2.5 h-2.5 rounded-full" style={{ background: DECISION_DOT[d.decision] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${DECISION_BADGE[d.decision]}`}>
                      {d.decision}
                    </span>
                    <span className="text-[13px] font-bold text-[#18365E]">{d.title}</span>
                    <span className="text-[10px] text-[#9ca3af]">under {d.runLabel}</span>
                  </div>
                  <div className="text-[11.5px] leading-relaxed text-[#374151] mt-1.5">&ldquo;{d.rationale}&rdquo;</div>
                  <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 mt-1.5 text-[10px] text-[#717182]">
                    <span>👤 <b className="text-[#374151]">{d.by}</b></span>
                    <span>{d.statusChange}</span>
                    {d.hasEvo && d.use_case_id != null && (
                      <button
                        onClick={() => setEvoId(d.use_case_id)}
                        className="text-[#18365E] font-semibold underline decoration-dotted hover:text-[#FF6E4C]"
                      >
                        View initiative evolution →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          <div className="px-5 py-2.5 bg-[#F5F7FA] text-[10.5px] text-[#9ca3af]">
            decision_log shape: decision_id · use_case_id · run/scenario · APPROVE|DEFER|KILL|HOLD · rationale · decided_by {'{'}role, name{'}'} · decided_at · status before→after
          </div>
        </div>
      </div>
      {evoId != null && <EvolutionView useCaseId={evoId} onClose={() => setEvoId(null)} />}
    </div>
  );
};

export default DecisionsSurface;
